import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Activity, AlertTriangle, Info, CheckCircle, XCircle, HelpCircle, ShoppingBag, ExternalLink, Sun, Moon, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Toaster } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DiagnosisResults = () => {
  const { diagnosisId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, language } = useLanguage();
  const [diagnosis, setDiagnosis] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if diagnosis was passed via state (guest mode)
    if (location.state?.diagnosis) {
      setDiagnosis(location.state.diagnosis);
      setLoading(false);
    } else if (diagnosisId) {
      // Fetch from API (logged in user)
      fetchDiagnosis();
    } else {
      navigate('/symptoms');
    }
  }, [diagnosisId, location.state]);

  const fetchDiagnosis = async () => {
    try {
      const response = await axios.get(`${API}/diagnoses/${diagnosisId}`);
      setDiagnosis(response.data);
    } catch (error) {
      console.error('Failed to fetch diagnosis:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'mild': return 'severity-mild';
      case 'moderate': return 'severity-moderate';
      case 'severe': return 'severity-severe';
      default: return '';
    }
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'mild': return t('mild');
      case 'moderate': return t('moderate');
      case 'severe': return t('severe');
      default: return severity;
    }
  };

  const getAnswerIcon = (answer) => {
    switch (answer) {
      case 'yes': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'no': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <HelpCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getAnswerLabel = (answer) => {
    switch (answer) {
      case 'yes': return language === 'pt' ? 'Sim' : 'Yes';
      case 'no': return language === 'pt' ? 'Não' : 'No';
      default: return language === 'pt' ? 'Não sei' : 'Unknown';
    }
  };

  // Guest header
  const GuestHeader = () => (
    <header className="fixed top-0 left-0 right-0 z-40 glass border-b border-border/50">
      <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <Activity className="w-5 h-5 text-primary-foreground" />
          </div>
          <span className="font-bold text-lg hidden sm:block">{t('appName')}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full"
          >
            {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => navigate('/auth')}
          >
            {t('signIn')}
          </Button>
        </div>
      </div>
    </header>
  );

  if (loading) {
    return (
      <>
        {!user && <GuestHeader />}
        <div className={`min-h-[80vh] flex items-center justify-center ${!user ? 'pt-16' : ''}`}>
          <Loader2 className="w-10 h-10 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (!diagnosis) return null;

  const isGuestMode = !user;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      {!user && <GuestHeader />}
      <div className={`max-w-2xl mx-auto px-6 py-8 ${!user ? 'pt-24 pb-8' : ''}`} data-testid="diagnosis-results">
        <Toaster position="top-center" />
        
        <Button
          variant="ghost"
          className="mb-6 rounded-full"
          onClick={() => navigate(user ? '/dashboard' : '/symptoms')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {user ? t('back') : t('newAssessment')}
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Guest sign-in prompt */}
          {isGuestMode && (
            <Card className="p-4 rounded-2xl border-primary/50 bg-primary/5 mb-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{t('saveYourResults')}</h3>
                  <p className="text-sm text-muted-foreground">{t('signInToSave')}</p>
                </div>
                <Button
                  className="rounded-full"
                  onClick={() => navigate('/auth?mode=register')}
                  data-testid="signup-prompt-btn"
                >
                  {t('signUpFree')}
                </Button>
              </div>
            </Card>
          )}

          {/* Header */}
          <div className="mb-8">
            <span className="label-medical">{t('assessmentResults')}</span>
            <h1 className="text-3xl font-bold mt-2">{diagnosis.symptom_name}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {t('completedOn')} {formatDate(diagnosis.created_at)}
            </p>
          </div>

          {/* Possible conditions */}
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              {t('possibleConditions')}
            </h2>

            {diagnosis.possible_conditions.length > 0 ? (
              <div className="space-y-4">
                {diagnosis.possible_conditions.map((condition, index) => (
                  <motion.div
                    key={condition.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Card className="p-5 rounded-2xl" data-testid={`condition-${index}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-lg">{condition.name}</h3>
                          <Badge className={`mt-1 ${getSeverityColor(condition.severity)}`}>
                            {getSeverityLabel(condition.severity)}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <span className="label-medical">{t('match')}</span>
                          <div className="text-2xl font-bold text-primary">{condition.match_score}%</div>
                        </div>
                      </div>
                      
                      {/* Match bar */}
                      <div className="match-bar mb-4">
                        <div className="match-bar-fill" style={{ width: `${condition.match_score}%` }} />
                      </div>

                      {condition.description && (
                        <p className="text-muted-foreground text-sm mb-3">{condition.description}</p>
                      )}

                      {condition.recommendations && (
                        <div className="bg-accent rounded-xl p-4">
                          <div className="flex items-start gap-2">
                            <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                            <div>
                              <span className="text-xs font-semibold uppercase text-primary">{t('recommendations')}</span>
                              <p className="text-sm mt-1">{condition.recommendations}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="p-6 rounded-2xl text-center">
                <AlertTriangle className="w-10 h-10 text-yellow-500 mx-auto mb-3" />
                <h3 className="font-semibold mb-1">{t('noMatchingConditions')}</h3>
                <p className="text-muted-foreground text-sm">
                  {t('noMatchingConditionsDesc')}
                </p>
              </Card>
            )}
          </div>

          {/* Product Recommendations */}
          {diagnosis.recommended_products && diagnosis.recommended_products.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-primary" />
                {t('recommendedProducts')}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {diagnosis.recommended_products.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                  >
                    <Card className="p-4 rounded-2xl overflow-hidden card-hover" data-testid={`product-${index}`}>
                      <div className="flex gap-3">
                        {product.image_url && (
                          <img 
                            src={product.image_url} 
                            alt={product.name}
                            className="w-16 h-16 object-cover rounded-xl bg-muted"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-sm truncate">{product.name}</h3>
                          {product.price && (
                            <div className="text-primary font-bold mt-0.5">{product.price}</div>
                          )}
                          {product.description && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{product.description}</p>
                          )}
                        </div>
                      </div>
                      {product.link && product.link !== '#' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-3 rounded-full"
                          onClick={() => window.open(product.link, '_blank')}
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1" />
                          {t('viewProduct')}
                        </Button>
                      )}
                    </Card>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <Card className="p-4 rounded-2xl border-yellow-500/50 bg-yellow-500/10 mb-8">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0" />
              <div>
                <h4 className="font-semibold text-sm">{t('medicalDisclaimer')}</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('medicalDisclaimerText')}
                </p>
              </div>
            </div>
          </Card>

          {/* Your answers */}
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-4">{t('yourResponses')}</h2>
            <div className="space-y-2">
              {diagnosis.answers.map((answer, index) => (
                <Card key={index} className="p-4 rounded-xl" data-testid={`answer-${index}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm flex-1 pr-4">{answer.question_text}</p>
                    <div className="flex items-center gap-2">
                      {getAnswerIcon(answer.answer)}
                      <span className="text-sm font-medium">{getAnswerLabel(answer.answer)}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={() => navigate('/symptoms')}
              data-testid="new-assessment-btn"
            >
              {t('newAssessment')}
            </Button>
            {user ? (
              <Button
                className="flex-1 rounded-full"
                onClick={() => navigate('/history')}
                data-testid="view-history-btn"
              >
                {t('viewHistory')}
              </Button>
            ) : (
              <Button
                className="flex-1 rounded-full"
                onClick={() => navigate('/auth?mode=register')}
                data-testid="signup-btn"
              >
                {t('signUpToSave')}
              </Button>
            )}
          </div>
        </motion.div>
      </div>
    </>
  );
};
